import React, { useState, useEffect } from 'react';
import { adminAPI, questionAPI } from '../services/api';
import toast from 'react-hot-toast';
import { resumeFileNameFromUrl, triggerBlobDownload } from '../utils/downloadFile';

const derivePrescreeningStatus = (sessions, hasResume) => {
  if (!sessions?.length) {
    if (!hasResume) {
      return { label: 'Not Started', key: 'not_started', detail: 'Resume required before prescreening can begin' };
    }
    return { label: 'Not Started', key: 'not_started', detail: 'Candidate has not started prescreening' };
  }
  const latest = sessions[0];
  const status = latest.sessionStatus;
  if (status === 'completed') {
    const scoreDetail =
      latest.overallScore != null ? `Score: ${Math.round(latest.overallScore)}%` : null;
    const passDetail =
      latest.isPassed === true ? 'Passed' : latest.isPassed === false ? 'Did not pass' : null;
    const detail = [scoreDetail, passDetail].filter(Boolean).join(' · ') || 'Prescreening completed';
    return { label: 'Completed', key: 'completed', detail };
  }
  if (status === 'in_progress') {
    const answered = latest.answeredQuestions ?? 0;
    const total = latest.totalQuestions ?? 0;
    return {
      label: 'In Progress',
      key: 'in_progress',
      detail: total > 0 ? `${answered} of ${total} questions answered` : 'Prescreening in progress',
    };
  }
  return { label: 'Incomplete', key: 'incomplete', detail: 'Prescreening was started but not finished' };
};

const deriveOverallAssessmentStatus = (assessments) => {
  if (!assessments?.length) {
    return { label: 'Pending', key: 'pending', detail: 'No skill assessments started yet' };
  }
  const normalized = assessments.map((a) => ({
    status: (a.status || '').toLowerCase(),
    completed: Boolean(
      a.status === 'completed' ||
        a.status === 'Completed' ||
        a.passed === true ||
        a.completed_at ||
        (a.completedAt && a.completedAt !== null)
    ),
  }));
  const completedCount = normalized.filter((a) => a.completed).length;
  const inProgressCount = normalized.filter(
    (a) => !a.completed && (a.status === 'in_progress' || a.status === 'started')
  ).length;

  if (inProgressCount > 0) {
    return {
      label: 'In Progress',
      key: 'in_progress',
      detail:
        completedCount > 0
          ? `${completedCount} completed, ${inProgressCount} in progress`
          : `${inProgressCount} assessment(s) in progress`,
    };
  }
  if (completedCount === assessments.length) {
    return {
      label: 'Completed',
      key: 'completed',
      detail: `${completedCount} assessment(s) completed`,
    };
  }
  if (completedCount > 0) {
    return {
      label: 'In Progress',
      key: 'in_progress',
      detail: `${completedCount} of ${assessments.length} completed`,
    };
  }
  return { label: 'Pending', key: 'pending', detail: 'Assessments assigned but not yet started' };
};

const candidateJobTitle = (candidate) => {
  const first = (candidate?.firstName || '').trim();
  const last = (candidate?.lastName || '').trim();
  const names = new Set(
    [first, last, `${first} ${last}`.trim(), `${last} ${first}`.trim()]
      .map((value) => value.toLowerCase())
      .filter(Boolean)
  );
  const options = [
    candidate?.jobTitle,
    candidate?.currentRole,
    candidate?.headline,
    candidate?.title,
  ];
  for (const value of options) {
    if (typeof value !== 'string') continue;
    const text = value.trim();
    if (text && !names.has(text.toLowerCase())) {
      return text;
    }
  }
  return 'N/A';
};

const statusBadgeClass = (key) => {
  switch (key) {
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-800';
    case 'incomplete':
      return 'bg-orange-100 text-orange-800';
    case 'not_started':
    case 'pending':
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const UserManagement = ({ lockedSection = null }) => {
  const [activeTab, setActiveTab] = useState(lockedSection || 'candidates');
  const [candidates, setCandidates] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [resumeDownloading, setResumeDownloading] = useState(false);
  const [candidateProgressLoading, setCandidateProgressLoading] = useState(false);
  const [candidateProgress, setCandidateProgress] = useState(null);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [showEmployerModal, setShowEmployerModal] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (lockedSection) {
      setActiveTab(lockedSection);
      setCurrentPage(1);
      setSearchTerm('');
    }
  }, [lockedSection]);

  useEffect(() => {
    if (activeTab === 'candidates') {
      fetchCandidates();
    } else if (activeTab === 'employers') {
      fetchEmployers();
    } else if (activeTab === 'employees') {
      fetchEmployees();
    }
  }, [activeTab, currentPage, searchTerm]);

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getUserStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    }
  };

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCandidates(currentPage, pageSize, searchTerm);
      setCandidates(response.data.candidates);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getEmployers(currentPage, pageSize, searchTerm);
      setEmployers(response.data.employers);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching employers:', error);
      toast.error('Failed to load employers');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getEmployees(currentPage, pageSize, searchTerm);
      setEmployees(response.data.employees || response.data);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const fetchCandidateProgress = async (candidate) => {
    const candidateId = candidate?.id;
    if (!candidateId) {
      setCandidateProgress(null);
      return;
    }
    setCandidateProgressLoading(true);
    setCandidateProgress(null);
    try {
      const hasResume = Boolean(candidate.resumeUrl || candidate.resume_url);
      const [sessionsRes, historyRes] = await Promise.all([
        questionAPI.getPrescreeningSessions(candidateId).catch(() => ({ data: [] })),
        questionAPI.getAssessmentHistory(candidateId).catch(() => ({ data: { assessments: [] } })),
      ]);
      const sessions = Array.isArray(sessionsRes.data) ? sessionsRes.data : [];
      const assessments = Array.isArray(historyRes.data?.assessments)
        ? historyRes.data.assessments
        : Array.isArray(historyRes.data)
          ? historyRes.data
          : [];
      setCandidateProgress({
        prescreening: derivePrescreeningStatus(sessions, hasResume),
        assessments: deriveOverallAssessmentStatus(assessments),
      });
    } catch (error) {
      console.error('Error fetching candidate progress:', error);
      setCandidateProgress({
        prescreening: { label: 'Unavailable', key: 'pending', detail: 'Could not load prescreening status' },
        assessments: { label: 'Unavailable', key: 'pending', detail: 'Could not load assessment status' },
      });
    } finally {
      setCandidateProgressLoading(false);
    }
  };

  const closeCandidateModal = () => {
    setShowCandidateModal(false);
    setSelectedCandidate(null);
    setCandidateProgress(null);
    setCandidateProgressLoading(false);
  };

  const handleToggleActive = async (userId, currentStatus, userName) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} ${userName}?`)) {
      return;
    }

    try {
      const response = await adminAPI.toggleUserActive(userId);
      toast.success(response.data.message);
      
      // Refresh the current list
      if (activeTab === 'candidates') {
        fetchCandidates();
      } else if (activeTab === 'employers') {
        fetchEmployers();
      } else if (activeTab === 'employees') {
        fetchEmployees();
      }
      
      // Refresh stats
      fetchStats();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };


  const pageTitle =
    lockedSection === 'employees'
      ? 'Users'
      : lockedSection === 'employers'
        ? 'Employers'
        : lockedSection === 'candidates'
          ? 'Candidates'
          : 'User Management';

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-secondary-900">{pageTitle}</h2>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="bg-gradient-to-br from-primary-50 to-white p-4 rounded-xl shadow-sm border border-primary-100">
            <h3 className="text-sm font-medium text-gray-600">Total Users</h3>
            <p className="text-2xl font-bold text-primary-600 mt-1">{stats.totalUsers}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-white p-4 rounded-xl shadow-sm border border-blue-100">
            <h3 className="text-sm font-medium text-gray-600">Candidates</h3>
            <p className="text-2xl font-bold text-blue-600 mt-1">{stats.totalCandidates}</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-white p-4 rounded-xl shadow-sm border border-green-100">
            <h3 className="text-sm font-medium text-gray-600">Employers</h3>
            <p className="text-2xl font-bold text-green-600 mt-1">{stats.totalEmployers}</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-white p-4 rounded-xl shadow-sm border border-yellow-100">
            <h3 className="text-sm font-medium text-gray-600">Employees</h3>
            <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.totalEmployees || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-accent-50 to-white p-4 rounded-xl shadow-sm border border-accent-100">
            <h3 className="text-sm font-medium text-gray-600">Active Users</h3>
            <p className="text-2xl font-bold text-accent-600 mt-1">{stats.activeUsers}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-white p-4 rounded-xl shadow-sm border border-purple-100">
            <h3 className="text-sm font-medium text-gray-600">Verified</h3>
            <p className="text-2xl font-bold text-purple-600 mt-1">{stats.verifiedUsers}</p>
          </div>
        </div>
      )}

      {/* Tabs and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Tabs (hidden when a single list is opened from admin top nav) */}
            {!lockedSection && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleTabChange('candidates')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'candidates'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Candidates
                </button>
                <button
                  onClick={() => handleTabChange('employers')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'employers'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Employers
                </button>
                <button
                  onClick={() => handleTabChange('employees')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'employees'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Employees
                </button>
              </div>
            )}

            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full md:w-80"
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {activeTab === 'candidates' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Experience
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {candidates.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                          No candidates found
                        </td>
                      </tr>
                    ) : (
                      candidates.map((candidate) => (
                        <tr key={candidate.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">
                              {candidate.firstName} {candidate.lastName}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {candidateJobTitle(candidate)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {candidate.experienceYears ? `${candidate.experienceYears} years` : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex space-x-2">
                              <button
                                onClick={async () => {
                                  setSelectedCandidate(candidate);
                                  setShowCandidateModal(true);
                                  fetchCandidateProgress(candidate);
                                  const uid = candidate.userId || candidate.user_id;
                                  if (uid) {
                                    try {
                                      const res = await adminAPI.getCandidateByUserId(uid);
                                      const profile = res.data?.profile ?? {};
                                      const merged = {
                                        ...candidate,
                                        ...profile,
                                        userId: profile.userId ?? profile.user_id ?? uid,
                                        resumeUrl: profile.resumeUrl ?? profile.resume_url ?? candidate.resumeUrl,
                                      };
                                      setSelectedCandidate(merged);
                                      fetchCandidateProgress(merged);
                                    } catch {
                                      /* list data still shown */
                                    }
                                  }
                                }}
                                className="px-3 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                                title="View candidate details"
                              >
                                View Details
                              </button>
                              <button
                                onClick={() => handleToggleActive(candidate.userId, candidate.isActive, `${candidate.firstName} ${candidate.lastName}`)}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                  candidate.isActive
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                                }`}
                              >
                                {candidate.isActive ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
              {activeTab === 'employers' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company Size
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Industry
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {employers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                          No employers found
                        </td>
                      </tr>
                    ) : (
                      employers.map((employer) => (
                        <tr key={employer.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">
                              {employer.firstName} {employer.lastName}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {employer.companyName || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {employer.companySize || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {employer.industry || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedEmployer(employer);
                                  setShowEmployerModal(true);
                                }}
                                className="px-3 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                                title="View employer details"
                              >
                                View Details
                              </button>
                              <button
                                onClick={() => handleToggleActive(employer.userId, employer.isActive, `${employer.firstName} ${employer.lastName}`)}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                  employer.isActive
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                                }`}
                              >
                                {employer.isActive ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
              {activeTab === 'employees' && (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Registered
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {employees.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                          No employees found
                        </td>
                      </tr>
                    ) : (
                      employees.map((employee) => (
                        <tr key={employee.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">
                              {employee.firstName} {employee.lastName}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {employee.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {employee.phone || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {employee.companyName || employee.employer?.companyName || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {employee.role === 'hr_recruiter' ? 'HR Recruiter' : 
                             employee.role === 'hr_manager' ? 'HR Manager' : 
                             employee.role || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                employee.isActive !== false
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {employee.isActive !== false ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(employee.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleToggleActive(employee.userId || employee.id, employee.isActive !== false, `${employee.firstName} ${employee.lastName}`)}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                  employee.isActive !== false
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                                }`}
                              >
                                {employee.isActive !== false ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Candidate Details Modal */}
      {showCandidateModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Candidate Details</h3>
              <button
                onClick={closeCandidateModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Personal Information</h4>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Full Name</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {selectedCandidate.firstName} {selectedCandidate.lastName}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedCandidate.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedCandidate.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Address</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedCandidate.address || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <div className="mt-1">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          selectedCandidate.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {selectedCandidate.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Registered Date</label>
                    <p className="text-sm text-gray-900 mt-1">{formatDate(selectedCandidate.createdAt)}</p>
                  </div>
                </div>

                {/* Professional Information */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Professional Information</h4>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Title</label>
                    <p className="text-sm text-gray-900 mt-1">{candidateJobTitle(selectedCandidate)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Experience</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {selectedCandidate.experienceYears ? `${selectedCandidate.experienceYears} years` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Profile Completion</label>
                    <div className="mt-1">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full"
                            style={{ width: `${selectedCandidate.profileCompletion || 0}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">{selectedCandidate.profileCompletion || 0}%</span>
                      </div>
                    </div>
                  </div>
                  {selectedCandidate.skills && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Skills</label>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {Array.isArray(selectedCandidate.skills) ? (
                          selectedCandidate.skills.map((skill, idx) => (
                            <span key={idx} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                              {skill}
                            </span>
                          ))
                        ) : (
                          <p className="text-sm text-gray-900">{selectedCandidate.skills}</p>
                        )}
                      </div>
                    </div>
                  )}
                  {selectedCandidate.bio && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Bio</label>
                      <p className="text-sm text-gray-900 mt-1">{selectedCandidate.bio}</p>
                    </div>
                  )}
                  {selectedCandidate.summary && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Summary</label>
                      <p className="text-sm text-gray-900 mt-1">{selectedCandidate.summary}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Assessment Progress */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Assessment Progress</h4>
                {candidateProgressLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                    Loading assessment status…
                  </div>
                ) : candidateProgress ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                      <label className="text-sm font-medium text-gray-500">Pre-screening</label>
                      <div className="mt-2 flex items-center gap-2">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${statusBadgeClass(candidateProgress.prescreening.key)}`}
                        >
                          {candidateProgress.prescreening.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">{candidateProgress.prescreening.detail}</p>
                    </div>
                    <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                      <label className="text-sm font-medium text-gray-500">Skill Assessments (Overall)</label>
                      <div className="mt-2 flex items-center gap-2">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${statusBadgeClass(candidateProgress.assessments.key)}`}
                        >
                          {candidateProgress.assessments.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">{candidateProgress.assessments.detail}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Assessment status unavailable for this candidate.</p>
                )}
              </div>

              {/* Additional Information */}
              {(selectedCandidate.education || selectedCandidate.linkedinUrl || selectedCandidate.resumeUrl) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedCandidate.education && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Education</label>
                        <p className="text-sm text-gray-900 mt-1">{selectedCandidate.education}</p>
                      </div>
                    )}
                    {selectedCandidate.linkedinUrl && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">LinkedIn</label>
                        <p className="text-sm text-gray-900 mt-1">
                          <a href={selectedCandidate.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {selectedCandidate.linkedinUrl}
                          </a>
                        </p>
                      </div>
                    )}
                    {selectedCandidate.resumeUrl && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Resume</label>
                        <p className="text-sm text-gray-900 mt-1">
                          <button
                            type="button"
                            disabled={resumeDownloading}
                            onClick={async () => {
                              const uid = selectedCandidate.userId || selectedCandidate.user_id;
                              if (!uid) {
                                toast.error('No resume available');
                                return;
                              }
                              try {
                                setResumeDownloading(true);
                                const res = await adminAPI.downloadCandidateResumeByUserId(uid);
                                triggerBlobDownload(
                                  res.data,
                                  resumeFileNameFromUrl(selectedCandidate.resumeUrl)
                                );
                                toast.success('Resume downloaded');
                              } catch (e) {
                                console.error(e);
                                toast.error(e.response?.data?.detail || 'Could not download resume');
                              } finally {
                                setResumeDownloading(false);
                              }
                            }}
                            className="text-blue-600 hover:underline disabled:opacity-50"
                          >
                            {resumeDownloading ? 'Downloading…' : 'Download Resume'}
                          </button>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={closeCandidateModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleToggleActive(selectedCandidate.userId, selectedCandidate.isActive, `${selectedCandidate.firstName} ${selectedCandidate.lastName}`);
                    closeCandidateModal();
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCandidate.isActive
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {selectedCandidate.isActive ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employer Details Modal */}
      {showEmployerModal && selectedEmployer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Employer Details</h3>
              <button
                onClick={() => {
                  setShowEmployerModal(false);
                  setSelectedEmployer(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Personal Information</h4>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Full Name</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {selectedEmployer.firstName} {selectedEmployer.lastName}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedEmployer.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedEmployer.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <div className="mt-1">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          selectedEmployer.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {selectedEmployer.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Verified</label>
                    <div className="mt-1">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          selectedEmployer.isVerified
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {selectedEmployer.isVerified ? 'Verified' : 'Unverified'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Registered Date</label>
                    <p className="text-sm text-gray-900 mt-1">{formatDate(selectedEmployer.createdAt)}</p>
                  </div>
                </div>

                {/* Company Information */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Company Information</h4>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Company Name</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedEmployer.companyName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Industry</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedEmployer.industry || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Company Size</label>
                    <p className="text-sm text-gray-900 mt-1">{selectedEmployer.companySize || 'N/A'}</p>
                  </div>
                  {selectedEmployer.companyWebsite && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Company Website</label>
                      <p className="text-sm text-gray-900 mt-1">
                        <a href={selectedEmployer.companyWebsite} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {selectedEmployer.companyWebsite}
                        </a>
                      </p>
                    </div>
                  )}
                  {selectedEmployer.companyAddress && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Company Address</label>
                      <p className="text-sm text-gray-900 mt-1">{selectedEmployer.companyAddress}</p>
                    </div>
                  )}
                  {selectedEmployer.companyDescription && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Company Description</label>
                      <p className="text-sm text-gray-900 mt-1">{selectedEmployer.companyDescription}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              {(selectedEmployer.address || selectedEmployer.bio || selectedEmployer.linkedinUrl) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedEmployer.address && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Address</label>
                        <p className="text-sm text-gray-900 mt-1">{selectedEmployer.address}</p>
                      </div>
                    )}
                    {selectedEmployer.bio && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Bio</label>
                        <p className="text-sm text-gray-900 mt-1">{selectedEmployer.bio}</p>
                      </div>
                    )}
                    {selectedEmployer.linkedinUrl && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">LinkedIn</label>
                        <p className="text-sm text-gray-900 mt-1">
                          <a href={selectedEmployer.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {selectedEmployer.linkedinUrl}
                          </a>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowEmployerModal(false);
                    setSelectedEmployer(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    handleToggleActive(selectedEmployer.userId, selectedEmployer.isActive, `${selectedEmployer.firstName} ${selectedEmployer.lastName}`);
                    setShowEmployerModal(false);
                    setSelectedEmployer(null);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedEmployer.isActive
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {selectedEmployer.isActive ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;


